import { useCallback, useEffect, useRef, useState } from "react";
import { supabase, friendlyError } from "./supabase.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import { useLiveRows, useFallbackPoll } from "./useLive.js";
import { applyRowChange } from "./liveRows.js";

/* Trackers, their rows and their saved views.
 *
 * Rows are fetched in pages because a tracker is whatever size the workbook
 * was: the pole tracker is 125 rows, the aging raw data is 142, and the next
 * one nobody has asked for yet could be thousands. Supabase caps a response at
 * a thousand rows by default, so a tracker that grew past it would quietly
 * chart three quarters of itself.
 */
const PAGE = 1000;

export function useTrackers(contractId) {
  const [trackers, setTrackers] = useState(null);
  const [error, setError] = useState("");
  const { user } = useAuth();

  const load = useCallback(async () => {
    if (!contractId) { setTrackers([]); return; }
    const { data, error } = await supabase
      .from("trackers")
      .select("*")
      .eq("contract_id", contractId)
      .is("deleted_at", null)
      .order("title");
    if (error) setError(friendlyError(error));
    else { setTrackers(data ?? []); setError(""); }
  }, [contractId]);

  useEffect(() => { setTrackers(null); load(); }, [load]);

  useLiveRows("trackers", contractId ? `contract_id=eq.${contractId}` : null, load);

  /* A tracker with no workbook behind it. source_file and source_sheet stay
     empty, which is how the rest of the app tells the two apart: one is a copy
     of a file and can be replaced from it, the other exists only here. */
  const create = useCallback(async ({ title, description = null, columns = [], rows = [] }) => {
    const clean = String(title ?? "").trim();
    if (!clean) { setError("A tracker needs a name."); return null; }
    if (!columns.length) { setError("A tracker needs at least one column."); return null; }
    const { data, error } = await supabase.from("trackers").insert({
      contract_id: contractId,
      title: clean,
      description: description || null,
      columns,
      row_count: rows.length,
      added_by: user?.id ?? null,
      updated_by: user?.id ?? null,
    }).select("id").single();
    if (error) { setError(friendlyError(error)); return null; }

    /* A tracker that lines up with a map layer starts with one row per feature,
       because the rows are the join and typing twenty eight school codes by
       hand is not a feature. Every column in them is still whatever was asked
       for and still starts empty. */
    if (rows.length) {
      const { error: rowError } = await supabase.from("tracker_rows").insert(
        rows.map((r, i) => ({
          tracker_id: data.id,
          contract_id: contractId,
          row_index: i,
          data: r,
          added_by: user?.id ?? null,
          updated_by: user?.id ?? null,
        })));
      if (rowError) { setError(friendlyError(rowError)); await load(); return data.id; }
    }

    await load();
    return data.id;
  }, [contractId, user, load]);

  return { trackers, error, reload: load, create };
}

export function useTracker(trackerId) {
  const { user } = useAuth();
  const [tracker, setTracker] = useState(null);
  const [rows, setRows] = useState(null);
  const [views, setViews] = useState(null);
  const [error, setError] = useState("");

  /* The mutations need the rows as they are at the moment of the click, not as
     they were when the callback was made. */
  const rowsRef = useRef(null);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  const load = useCallback(async () => {
    if (!trackerId) { setTracker(null); setRows([]); setViews([]); return; }

    const [t, v] = await Promise.all([
      supabase.from("trackers").select("*").eq("id", trackerId).single(),
      supabase.from("tracker_views").select("*").eq("tracker_id", trackerId)
        .is("deleted_at", null).order("position"),
    ]);
    if (t.error) { setError(friendlyError(t.error)); return; }
    setTracker(t.data);
    setViews(v.data ?? []);

    const all = [];
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await supabase
        .from("tracker_rows")
        .select("id, row_index, data")
        .eq("tracker_id", trackerId)
        .is("deleted_at", null)
        .order("row_index")
        .range(from, from + PAGE - 1);
      if (error) { setError(friendlyError(error)); return; }
      all.push(...(data ?? []));
      if (!data || data.length < PAGE) break;
    }
    setRows(all.map((r) => ({ ...r.data, _id: r.id, _index: r.row_index })));
    setError("");
  }, [trackerId]);

  useEffect(() => { setTracker(null); setRows(null); load(); }, [load]);

  const saveView = useCallback(async (view) => {
    const payload = {
      contract_id: tracker.contract_id,
      tracker_id: tracker.id,
      title: view.title,
      spec: view.spec,
      position: view.position ?? (views?.length ?? 0),
    };
    const { error } = view.id
      ? await supabase.from("tracker_views").update(payload).eq("id", view.id)
      : await supabase.from("tracker_views").insert(payload);
    if (error) { setError(friendlyError(error)); return false; }
    await load();
    return true;
  }, [tracker, views, load]);

  /* Renaming after the fact, because the name a sheet came in with is rarely
     the name anybody would look for it under. */
  const rename = useCallback(async (title, description) => {
    const clean = String(title ?? "").trim();
    if (!clean) { setError("A tracker needs a name."); return false; }
    const { error } = await supabase.from("trackers")
      .update({ title: clean, description: description ?? tracker?.description ?? null })
      .eq("id", trackerId);
    if (error) { setError(friendlyError(error)); return false; }
    await load();
    return true;
  }, [trackerId, tracker, load]);

  /* Somebody else changed a row.
   *
   * This one is patched from the payload rather than refetched. A register is
   * a few hundred rows and a refetch costs one request; a tracker is whatever
   * size the workbook was, and refetching several pages of it every time
   * another person leaves a cell would make their typing feel like weather.
   *
   * The row arrives whole, so nothing has to be worked out: an insert is
   * appended in its own index order, an update replaces or removes depending
   * on whether it carries a deleted_at, and a hard delete carries the id,
   * which is all that is needed to drop it.
   */
  const onRowChange = useCallback(
    (payload) => setRows((cur) => applyRowChange(cur, payload)), []);

  const live = useLiveRows("tracker_rows", trackerId ? `tracker_id=eq.${trackerId}` : null, onRowChange);
  useFallbackPoll(load, live);

  /* The tracker itself: a rename, a description, a change to the columns.
     One row, so it is simply read again. */
  useLiveRows("trackers", trackerId ? `id=eq.${trackerId}` : null,
    useCallback((payload) => {
      if (payload.new) setTracker(payload.new);
    }, []));

  /* A saved view somebody else added or removed. */
  useLiveRows("tracker_views", trackerId ? `tracker_id=eq.${trackerId}` : null, load);

  /* ------------------------------------------------------------ editing
   *
   * Every one of these changes the screen first and the database second. A
   * tracker is edited while somebody is talking, and a cell that waits half a
   * second for a round trip before showing the new value reads as a dropped
   * keystroke. If the write fails the value goes back to what it was and the
   * error says so, which is the honest version of optimism.
   *
   * A cell writes the whole row rather than the one key, because Postgres
   * jsonb cannot be merged through this client without a stored procedure.
   * Two people editing different cells of the same row in the same second
   * means the later write wins the row; different rows never collide. For a
   * team of two to six that is the right trade, and it is worth knowing.
   */
  const bare = (row) => {
    const { _id, _index, ...data } = row;
    return data;
  };

  const setCell = useCallback(async (rowId, key, value) => {
    const before = rowsRef.current;
    const row = before?.find((r) => r._id === rowId);
    if (!row) return false;
    const next = { ...row, [key]: value };
    setRows(before.map((r) => (r._id === rowId ? next : r)));

    const { error } = await supabase.from("tracker_rows")
      .update({ data: bare(next), updated_by: user?.id ?? null })
      .eq("id", rowId);
    if (error) {
      setRows(before);
      setError(friendlyError(error));
      return false;
    }
    setError("");
    return true;
  }, [user]);

  const addRow = useCallback(async (data = {}) => {
    const before = rowsRef.current ?? [];
    const index = before.reduce((m, r) => Math.max(m, r._index), -1) + 1;
    const { data: ins, error } = await supabase.from("tracker_rows").insert({
      tracker_id: trackerId,
      contract_id: tracker.contract_id,
      row_index: index,
      data,
      added_by: user?.id ?? null,
      updated_by: user?.id ?? null,
    }).select("id, row_index, data").single();
    if (error) { setError(friendlyError(error)); return false; }

    setRows([...before, { ...ins.data, _id: ins.id, _index: ins.row_index }]);
    await countRows(before.length + 1);
    setError("");
    return true;
  }, [trackerId, tracker, user]);

  const removeRow = useCallback(async (rowId) => {
    const before = rowsRef.current ?? [];
    setRows(before.filter((r) => r._id !== rowId));
    const { error } = await supabase.from("tracker_rows")
      .update({ deleted_at: new Date().toISOString(), updated_by: user?.id ?? null })
      .eq("id", rowId);
    if (error) { setRows(before); setError(friendlyError(error)); return false; }
    await countRows(before.length - 1);
    setError("");
    return true;
  }, [user]);

  /* row_count is on the tracker so the list page can show it without reading
     every row. It is a cache, so it is corrected on every add and remove
     rather than being recomputed anywhere. */
  const countRows = async (n) => {
    await supabase.from("trackers").update({ row_count: n }).eq("id", trackerId);
    setTracker((t) => (t ? { ...t, row_count: n } : t));
  };

  const setColumns = useCallback(async (columns) => {
    const { error } = await supabase.from("trackers")
      .update({ columns, updated_by: user?.id ?? null }).eq("id", trackerId);
    if (error) { setError(friendlyError(error)); return false; }
    setTracker((t) => (t ? { ...t, columns } : t));
    setError("");
    return true;
  }, [trackerId, user]);

  const removeView = useCallback(async (id) => {
    const { error } = await supabase.from("tracker_views")
      .update({ deleted_at: new Date().toISOString() }).eq("id", id);
    if (error) { setError(friendlyError(error)); return false; }
    await load();
    return true;
  }, [load]);

  return {
    tracker, rows, views, error, reload: load,
    saveView, removeView, rename,
    setCell, addRow, removeRow, setColumns,
    live,
  };
}
